import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, type DragOverEvent, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable';
import { Button, MessageStrip, BusyIndicator, Text, FlexBox, Icon } from '@ui5/webcomponents-react';
import { api } from '../../api/client';
import { SortableChip, DropZone } from './designer/dnd';
import { PageHeader } from '../../components/Responsive';

// Groups from locked system packs sort last and start collapsed (rarely rearranged on a menu).
const LOCKED = new Set(['General Setup', 'Administration']);
const BAND = 10000; // per-group order band (supports up to ~999 screens/group)

/** Drag-and-drop menu layout: reorder screens within a module, drag them between modules, and reorder the
 * modules. "Save layout" writes group + order to SAP in one call (only the screens that actually moved). */
export function MenuDesigner() {
  const qc = useQueryClient();
  const screens = useQuery({ queryKey: ['screens-all'], queryFn: api.screens });
  const [groups, setGroups] = useState<string[]>([]);
  const [items, setItems] = useState<Record<string, string[]>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const byId = useMemo(() => new Map((screens.data ?? []).map(s => [s.id, s])), [screens.data]);
  const original = useMemo(() => {
    const m = new Map<string, { group: string; order: number }>();
    for (const s of screens.data ?? []) m.set(s.id, { group: s.menu?.group ?? '', order: s.menu?.order ?? 0 });
    return m;
  }, [screens.data]);

  useEffect(() => {
    if (!screens.data) return;
    const g = new Map<string, { min: number; ids: { id: string; order: number }[] }>();
    for (const s of screens.data) {
      const gr = s.menu?.group || 'Ungrouped', o = s.menu?.order ?? 999;
      const e = g.get(gr) ?? { min: Infinity, ids: [] }; e.min = Math.min(e.min, o); e.ids.push({ id: s.id, order: o }); g.set(gr, e);
    }
    const order = [...g.entries()].sort((a, b) => (LOCKED.has(a[0]) ? 1 : 0) - (LOCKED.has(b[0]) ? 1 : 0) || a[1].min - b[1].min || a[0].localeCompare(b[0]));
    setGroups(order.map(([n]) => n));
    const it: Record<string, string[]> = {};
    for (const [n, e] of order) it[n] = e.ids.sort((a, b) => a.order - b.order || (byId.get(a.id)?.title ?? '').localeCompare(byId.get(b.id)?.title ?? '')).map(x => x.id);
    setItems(it);
    setCollapsed(new Set(order.map(([n]) => n))); // start with every module collapsed — expand what you edit
  }, [screens.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const findContainer = (id: string) => id.startsWith('grp::') ? id.slice(5) : groups.find(g => items[g]?.includes(id));

  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e; if (!over) return;
    const from = findContainer(String(active.id)), to = findContainer(String(over.id));
    if (!from || !to || from === to) return;
    setItems(prev => {
      const fromArr = prev[from].filter(x => x !== active.id), toArr = [...prev[to]];
      let idx = String(over.id).startsWith('grp::') ? toArr.length : toArr.indexOf(String(over.id));
      if (idx < 0) idx = toArr.length;
      toArr.splice(idx, 0, String(active.id));
      return { ...prev, [from]: fromArr, [to]: toArr };
    });
  };
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e; if (!over) return;
    const from = findContainer(String(active.id)), to = findContainer(String(over.id));
    if (from && to && from === to && active.id !== over.id && !String(over.id).startsWith('grp::')) {
      const arr = items[from], oi = arr.indexOf(String(active.id)), ni = arr.indexOf(String(over.id));
      if (oi >= 0 && ni >= 0) setItems({ ...items, [from]: arrayMove(arr, oi, ni) });
    }
  };
  const moveGroup = (i: number, dir: -1 | 1) => { const j = i + dir; if (j < 0 || j >= groups.length) return; const g = [...groups]; [g[i], g[j]] = [g[j], g[i]]; setGroups(g); };
  const toggle = (g: string) => setCollapsed(c => { const n = new Set(c); n.has(g) ? n.delete(g) : n.add(g); return n; });

  const save = async () => {
    setErr(null); setBusy(true);
    const changed: { id: string; group: string; order: number }[] = [];
    groups.forEach((g, gi) => (items[g] ?? []).forEach((id, si) => {
      const order = gi * BAND + (si + 1) * 10, o = original.get(id);
      if (!o || o.group !== g || o.order !== order) changed.push({ id, group: g, order });
    }));
    try {
      if (changed.length) { const r = await api.reorderScreens(changed); setMsg(`Saved — ${r.updated} screen(s) updated.`); }
      else setMsg('No changes to save.');
      await qc.invalidateQueries({ queryKey: ['screens'] }); await qc.invalidateQueries({ queryKey: ['screens-all'] });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  if (screens.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;

  return (
    <div>
      <PageHeader title="Menu Layout" style={{ marginBottom: '0.5rem' }} actions={<>
        <Button design="Transparent" icon="expand" onClick={() => setCollapsed(new Set())}>Expand all</Button>
        <Button design="Transparent" icon="collapse" onClick={() => setCollapsed(new Set(groups))}>Collapse all</Button>
        <Button design="Emphasized" icon="save" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save layout'}</Button>
      </>} />
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '0.75rem' }}>
        Drag a screen to reorder it, or onto another module to move it. Use the ↑ ↓ arrows to reorder modules. Click <b>Save layout</b> to store in SAP (only moved screens are written). Create a new module by typing its name in the <b>Menu &amp; Access</b> tab.
      </MessageStrip>
      {err && <MessageStrip design="Negative" onClose={() => setErr(null)}>{err}</MessageStrip>}
      {msg && <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '0.5rem' }}>{msg}</MessageStrip>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragOver={onDragOver} onDragEnd={onDragEnd}>
        {groups.map((g, gi) => (
          <div key={g} style={{ border: '1px solid var(--sapNeutralBorderColor,#d9d9d9)', borderRadius: '0.5rem', marginBottom: '0.6rem', overflow: 'hidden' }}>
            <FlexBox alignItems="Center" style={{ gap: '0.25rem', padding: '0.35rem 0.5rem', background: 'var(--sapList_HeaderBackground,#f2f3f4)' }}>
              <Button design="Transparent" icon={collapsed.has(g) ? 'slim-arrow-right' : 'slim-arrow-down'} onClick={() => toggle(g)} />
              <Text style={{ fontWeight: 600 }}>{g}</Text>
              <Text style={{ color: 'var(--sapNeutralTextColor)' }}>({items[g]?.length ?? 0})</Text>
              <div style={{ marginLeft: 'auto', display: 'flex' }}>
                <Button design="Transparent" icon="slim-arrow-up" tooltip="Move module up" disabled={gi === 0} onClick={() => moveGroup(gi, -1)} />
                <Button design="Transparent" icon="slim-arrow-down" tooltip="Move module down" disabled={gi === groups.length - 1} onClick={() => moveGroup(gi, 1)} />
              </div>
            </FlexBox>
            {!collapsed.has(g) && (
              <DropZone id={`grp::${g}`} style={{ padding: '0.5rem', minHeight: '2.5rem' }}>
                <SortableContext items={items[g] ?? []} strategy={rectSortingStrategy}>
                  <FlexBox wrap="Wrap" style={{ gap: '0.4rem' }}>
                    {(items[g] ?? []).map(id => { const s = byId.get(id); return (
                      <SortableChip key={id} id={id}>
                        <FlexBox alignItems="Center" style={{ gap: '0.35rem' }}><Icon name={s?.icon ?? 'document'} /><span>{s?.title ?? id}</span></FlexBox>
                      </SortableChip>
                    ); })}
                    {(items[g]?.length ?? 0) === 0 && <Text style={{ color: 'var(--sapNeutralTextColor)', padding: '0.25rem' }}>Drop screens here</Text>}
                  </FlexBox>
                </SortableContext>
              </DropZone>
            )}
          </div>
        ))}
      </DndContext>
    </div>
  );
}
